import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
    Bot, ArrowUp, X as XIcon, CheckCircle2, AlertTriangle, Loader2,
    Clock, RefreshCw, ListChecks,
} from "lucide-react";

/**
 * Floating AI Parse Queue widget.
 *
 * Rendered globally in AppShell, visible on every authenticated page (except
 * for interviewer/seeker portals which use their own shells).
 *
 * Behaviour:
 *  • Always shows a small circular badge in the bottom-right corner with the
 *    current "running + queued" count. Click to open the dialog.
 *  • Dialog polls `GET /api/ai-queue` every 4 seconds while open so recruiters
 *    see live progress without re-loading the page.
 *  • Each queued row exposes Prioritize (jump to front) and Cancel buttons.
 *  • Recent failures expose a Retry button (calls /candidates/{cid}/reparse).
 *  • Auto-hides if there's no activity AND no recent items in the last 5 min.
 *  • Hidden for admin/interviewer/seeker roles.
 */
export default function AIQueueWidget() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [snapshot, setSnapshot] = useState({ running: [], queued: [], recent: [], stats: { running_count: 0, queued_count: 0, avg_seconds: 0, concurrency: 1 } });
    const [loading, setLoading] = useState(false);
    const [busyById, setBusyById] = useState({});

    const isRecruiter = user && ["employer", "recruiter"].includes(user.role);

    // Phase 56.1 — completion badge state. Tracks newly-finished items since
    // the dialog was last opened. Persists across page navigations via a
    // window-scoped ref so a recruiter who moves from /candidates to /jobs
    // keeps seeing accumulated completions.
    const [completionBadge, setCompletionBadge] = useState(0);
    const seenCompletionIdsRef = useRef(new Set());
    // Hydrate from localStorage so the badge survives a hard refresh
    useEffect(() => {
        try {
            const stored = localStorage.getItem("ai_queue_seen_completions");
            if (stored) {
                const arr = JSON.parse(stored);
                if (Array.isArray(arr)) seenCompletionIdsRef.current = new Set(arr);
            }
        } catch { /* ignore */ }
    }, []);

    const fetchSnapshot = useCallback(async () => {
        if (!isRecruiter) return;
        setLoading(true);
        try {
            const { data } = await api.get("/ai-queue");
            setSnapshot(data || { running: [], queued: [], recent: [], stats: {} });

            // Detect newly-completed items (state === "done") and emit a
            // window event so individual pages (e.g. JobView) can surface a
            // one-click "refresh for new results" pill without polling.
            const recent = (data?.recent || []);
            const newDones = recent.filter(
                (r) => (r.state === "done") && r.id && !seenCompletionIdsRef.current.has(r.id)
            );
            if (newDones.length > 0) {
                for (const item of newDones) {
                    seenCompletionIdsRef.current.add(item.id);
                    window.dispatchEvent(new CustomEvent("ai-queue-completed", {
                        detail: {
                            queue_id: item.id,
                            candidate_id: item.candidate_id,
                            job_id: item.job_id,
                            candidate_name: item.candidate_name,
                        },
                    }));
                }
                // Persist the seen set (cap to last 500 ids to bound size)
                try {
                    const arr = [...seenCompletionIdsRef.current].slice(-500);
                    localStorage.setItem("ai_queue_seen_completions", JSON.stringify(arr));
                } catch { /* ignore */ }
                // Only bump the badge when the dialog is CLOSED — opening it
                // implicitly clears the badge.
                if (!open) {
                    setCompletionBadge((c) => c + newDones.length);
                    // Best-effort desktop notification (silently noops if denied)
                    try {
                        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                            const first = newDones[0];
                            const more = newDones.length > 1 ? ` (+${newDones.length - 1} more)` : "";
                            new Notification("AI parse completed", {
                                body: `${first.candidate_name || first.file_name || "Resume"} is ready${more}`,
                                tag: "ai-queue-completion",
                                silent: true,
                            });
                        }
                    } catch { /* ignore */ }
                }
            }
        } catch (err) {
            console.warn("ai-queue snapshot failed", err);
        } finally { setLoading(false); }
    }, [isRecruiter, open]);

    // Always poll lightly so the floating badge stays current. 10s when closed,
    // 4s when the dialog is open.
    useEffect(() => {
        if (!isRecruiter) return;
        fetchSnapshot();
        const interval = setInterval(fetchSnapshot, open ? 4000 : 10000);
        return () => clearInterval(interval);
    }, [isRecruiter, open, fetchSnapshot]);

    // Ask for notification permission once (lazily). Silent denial is fine.
    useEffect(() => {
        if (!isRecruiter) return;
        try {
            if (typeof Notification !== "undefined" && Notification.permission === "default") {
                // Defer to next tick so the page is interactive before the prompt
                setTimeout(() => { try { Notification.requestPermission(); } catch { /* ignore */ } }, 1500);
            }
        } catch { /* ignore */ }
    }, [isRecruiter]);

    // Clear the completion badge when the dialog is opened.
    useEffect(() => {
        if (open) setCompletionBadge(0);
    }, [open]);

    if (!isRecruiter) return null;

    const runningCount = snapshot?.stats?.running_count || 0;
    const queuedCount = snapshot?.stats?.queued_count || 0;
    const totalActive = runningCount + queuedCount;
    const avgSec = snapshot?.stats?.avg_seconds || 0;
    const recentFailures = (snapshot.recent || []).filter((r) => r.state === "failed").length;

    const setBusy = (id, val) => setBusyById((m) => ({ ...m, [id]: val }));

    const onPrioritize = async (qid) => {
        setBusy(qid, "priority");
        try {
            await api.post(`/ai-queue/${qid}/prioritize`);
            toast.success("Bumped to the front of the queue");
            fetchSnapshot();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(qid, null); }
    };

    const onCancel = async (qid) => {
        setBusy(qid, "cancel");
        try {
            await api.post(`/ai-queue/${qid}/cancel`);
            toast.success("Cancelled");
            fetchSnapshot();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(qid, null); }
    };

    const onRetry = async (cid) => {
        if (!cid) return;
        setBusy(cid, "retry");
        try {
            await api.post(`/candidates/${cid}/reparse`);
            toast.success("Re-queued for AI parse");
            fetchSnapshot();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(cid, null); }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="fixed z-50 bottom-5 right-5 group inline-flex items-center gap-2 pl-3 pr-3 py-2 bg-ikb text-white shadow-lg hover:bg-[#001f7a] border border-ikb/40 font-mono text-[11px] uppercase tracking-widest"
                data-testid="ai-queue-fab"
                title={`AI parsing queue · ${runningCount} running · ${queuedCount} queued`}
            >
                <Bot className="w-4 h-4" />
                <span>AI&nbsp;Queue</span>
                {totalActive > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-white text-ikb font-bold rounded-full" data-testid="ai-queue-fab-count">
                        {totalActive}
                    </span>
                )}
                {completionBadge > 0 && (
                    <span
                        className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-emerald-400 text-emerald-950 font-bold rounded-full animate-pulse"
                        data-testid="ai-queue-completion-badge"
                        title={`${completionBadge} parse${completionBadge === 1 ? "" : "s"} just finished — click to view`}
                    >
                        +{completionBadge}
                    </span>
                )}
                {totalActive === 0 && completionBadge === 0 && recentFailures > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-rose-100 text-rose-800 font-bold rounded-full" title={`${recentFailures} recent failure${recentFailures === 1 ? "" : "s"}`}>
                        {recentFailures}
                    </span>
                )}
                {runningCount > 0 && (
                    <span className="w-1.5 h-1.5 bg-amber-300 rounded-full animate-pulse" />
                )}
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl" data-testid="ai-queue-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bot className="w-4 h-4 text-ikb" />
                            AI Parsing Queue
                            <button
                                onClick={fetchSnapshot}
                                className="ml-auto text-[11px] font-mono uppercase tracking-widest border border-border px-2 py-1 hover:bg-secondary inline-flex items-center gap-1"
                                data-testid="ai-queue-refresh"
                                disabled={loading}
                            >
                                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                                Refresh
                            </button>
                        </DialogTitle>
                    </DialogHeader>

                    <Stats stats={snapshot.stats || {}} />

                    {/* Running */}
                    <Section title="Currently processing" empty="Idle — no resumes are being parsed right now." rows={snapshot.running} icon={Loader2}>
                        {(snapshot.running || []).map((item) => (
                            <QueueRow
                                key={item.id}
                                item={item}
                                avgSec={avgSec}
                                position={null}
                                state="running"
                            />
                        ))}
                    </Section>

                    {/* Queued */}
                    <Section title={`Queue · ${queuedCount}`} empty="Nothing queued behind." rows={snapshot.queued} icon={ListChecks}>
                        {(snapshot.queued || []).map((item, i) => (
                            <QueueRow
                                key={item.id}
                                item={item}
                                avgSec={avgSec}
                                position={i + 1}
                                state="queued"
                                onPrioritize={() => onPrioritize(item.id)}
                                onCancel={() => onCancel(item.id)}
                                busy={busyById[item.id]}
                            />
                        ))}
                    </Section>

                    {/* Recent */}
                    <Section title="Recently completed" empty="No recent activity." rows={snapshot.recent} icon={CheckCircle2}>
                        {(snapshot.recent || []).map((item) => (
                            <QueueRow
                                key={item.id}
                                item={item}
                                avgSec={avgSec}
                                position={null}
                                state={item.state}
                                onRetry={() => onRetry(item.candidate_id)}
                                busy={busyById[item.candidate_id]}
                            />
                        ))}
                    </Section>
                </DialogContent>
            </Dialog>
        </>
    );
}

function Stats({ stats }) {
    return (
        <div className="grid grid-cols-3 gap-3 my-2" data-testid="ai-queue-stats">
            <Stat label="Running" value={stats.running_count || 0} accent="amber" />
            <Stat label="Queued" value={stats.queued_count || 0} accent="ikb" />
            <Stat label="Avg parse" value={stats.avg_seconds ? `${formatDuration(stats.avg_seconds)}` : "—"} accent="muted" />
        </div>
    );
}

function Stat({ label, value, accent }) {
    const cls = accent === "amber" ? "border-amber-300 bg-amber-50/40 text-amber-900"
        : accent === "ikb" ? "border-ikb/30 bg-ikb/5 text-ikb"
        : "border-border bg-secondary/40 text-foreground/80";
    return (
        <div className={`border ${cls} px-3 py-2`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{label}</div>
            <div className="text-lg font-display font-bold">{value}</div>
        </div>
    );
}

function Section({ title, empty, rows, icon: Icon, children }) {
    const hasRows = (rows || []).length > 0;
    return (
        <div className="mt-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/60 flex items-center gap-1.5 border-b border-border pb-1">
                {Icon ? <Icon className="w-3 h-3" /> : null}
                {title}
            </div>
            {hasRows ? (
                <div className="max-h-[180px] overflow-y-auto pr-1 mt-1">
                    {children}
                </div>
            ) : (
                <div className="text-[12px] text-foreground/60 italic py-3">{empty}</div>
            )}
        </div>
    );
}

function QueueRow({ item, avgSec, position, state, onPrioritize, onCancel, onRetry, busy }) {
    const eta = useMemo(() => {
        if (!position || !avgSec) return null;
        return formatDuration(position * avgSec);
    }, [position, avgSec]);

    const stateColor = useMemo(() => {
        switch (state) {
            case "running": return "border-amber-300 bg-amber-50 text-amber-900";
            case "queued": return "border-ikb/30 bg-ikb/5 text-ikb";
            case "done": return "border-emerald-300 bg-emerald-50 text-emerald-800";
            case "failed": return "border-rose-300 bg-rose-50 text-rose-800";
            case "cancelled": return "border-border bg-secondary text-foreground/60";
            default: return "border-border";
        }
    }, [state]);

    return (
        <div className="flex items-center gap-2 py-1.5 border-b border-border/50 last:border-b-0" data-testid={`ai-queue-row-${item.id}`}>
            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${stateColor}`}>
                {state}
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-sm truncate font-medium flex items-center gap-1.5">
                    <span className="truncate">{item.candidate_name || item.file_name || item.candidate_id || "—"}</span>
                    <SourceBadge source={item.source} />
                </div>
                <div className="text-[11px] text-foreground/60 truncate">
                    {item.file_name ? <span title={item.file_name}>{item.file_name}</span> : <span className="opacity-60">{item.candidate_id}</span>}
                    {item.triggered_by_user_name ? <> · by <span className="text-foreground/80">{item.triggered_by_user_name}</span></> : null}
                    {state === "running" && item.started_at ? <> · started {timeAgo(item.started_at)}</> : null}
                    {state === "queued" && item.queued_at ? <> · queued {timeAgo(item.queued_at)}</> : null}
                    {(state === "done" || state === "failed" || state === "cancelled") && item.completed_at ? <> · {timeAgo(item.completed_at)}</> : null}
                    {item.priority > 0 ? <> · <span className="text-ikb font-bold">priority {item.priority}</span></> : null}
                </div>
                {item.error && state === "failed" ? (
                    <div className="text-[11px] text-rose-700 truncate" title={item.error}>
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {item.error}
                    </div>
                ) : null}
            </div>
            {state === "queued" && eta && (
                <span className="text-[11px] font-mono text-foreground/60 inline-flex items-center gap-1" title="Estimated wait based on recent parse times">
                    <Clock className="w-3 h-3" /> ~{eta}
                </span>
            )}
            {state === "queued" && (
                <>
                    <button
                        onClick={onPrioritize}
                        disabled={busy === "priority"}
                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-ikb text-ikb hover:bg-ikb/5 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`ai-queue-prioritize-${item.id}`}
                        title="Move this parse to the front of the queue"
                    >
                        <ArrowUp className="w-3 h-3" /> Top
                    </button>
                    <button
                        onClick={onCancel}
                        disabled={busy === "cancel"}
                        className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`ai-queue-cancel-${item.id}`}
                        title="Skip this resume — won't be parsed"
                    >
                        <XIcon className="w-3 h-3" /> Cancel
                    </button>
                </>
            )}
            {state === "failed" && onRetry && (
                <button
                    onClick={onRetry}
                    disabled={busy === "retry"}
                    className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`ai-queue-retry-${item.candidate_id}`}
                    title="Retry parsing this resume"
                >
                    <RefreshCw className={`w-3 h-3 ${busy === "retry" ? "animate-spin" : ""}`} /> Retry
                </button>
            )}
        </div>
    );
}

function SourceBadge({ source }) {
    if (!source || source === "unknown") return null;
    const meta = {
        upload: { label: "Upload", cls: "border-ikb/30 bg-ikb/5 text-ikb" },
        retry_parse: { label: "Retry", cls: "border-rose-300 bg-rose-50 text-rose-700" },
        bulk_retry: { label: "Bulk Retry", cls: "border-rose-300 bg-rose-50 text-rose-700" },
        bulk_upload: { label: "Bulk Upload", cls: "border-ikb/30 bg-ikb/5 text-ikb" },
        public_apply: { label: "Public Apply", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
        seeker_reupload: { label: "Seeker", cls: "border-purple-300 bg-purple-50 text-purple-800" },
    }[source] || { label: source, cls: "border-border bg-secondary text-foreground/70" };
    return (
        <span
            className={`text-[9px] font-mono uppercase tracking-widest px-1 py-0 border ${meta.cls}`}
            data-testid={`ai-queue-source-${source}`}
            title={`Triggered via ${meta.label}`}
        >
            {meta.label}
        </span>
    );
}

function formatDuration(seconds) {
    if (!seconds || seconds < 1) return "<1s";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function timeAgo(iso) {
    if (!iso) return "";
    try {
        const t = new Date(iso).getTime();
        if (!t) return "";
        const diff = (Date.now() - t) / 1000;
        if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
        if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
        return `${Math.round(diff / 86400)}d ago`;
    } catch { return ""; }
}
